import { Drawer } from '@tunein/react-ui';
import classNames from 'clsx';
import debounce from 'lodash/debounce';
import flowRight from 'lodash/flowRight';
import PropTypes from 'prop-types';
import { Component, createRef } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Link } from 'src/common/components/shared/link/Link';
import { IHEART_GUIDE_ID } from 'src/common/constants/affiliates/iHeartRadio';
import { withReactHooks } from 'src/common/decorators/withReactHooks';
import {
  selectBottomAdBannerHeight,
  selectIsDesktop,
  selectTopBannerHeight,
} from 'src/common/selectors/app';
import { selectShouldShowDesktopUpdateBanner } from 'src/common/selectors/desktop';
import { selectIsExpiredWebSubscriber, selectIsUserSubscribed } from 'src/common/selectors/me';
import { logWebActivity } from '../../actions/logging';
import { labels } from '../../constants/analytics/categoryActionLabel/subscribe';
import web from '../../constants/analytics/categoryActionLabel/web';
import { LEFT_SIDEBAR_FIXED_BOTTOM_MENU } from '../../constants/experiments/navigation';
import connectWithAuth from '../../decorators/auth/connectWithAuth';
import { LocationAndLocalizationContext } from '../../providers/LocationAndLocalizationProvider';
import { selectExperiment } from '../../selectors/config';
import cssVariables from '../../styles/variables';
import getTopBannerOffsetStyles from '../../utils/banner/getTopBannerOffsetStyles';
import { getCssStyle } from '../../utils/getCssStyle';
import { appendToLabel } from '../../utils/logging';
import { useShouldShowPlayer } from '../../utils/playerStatus/shouldShowPlayer';
import getSubscribeEventLabel from '../../utils/subscription/getSubscribeEventLabel';
import Scrollbar from '../Scrollbar';
import TuneInMarquee from '../shared/svgIcons/TuneInMarquee';
import UpsellButton from '../subscription/UpsellButton';
import BottomMenu from './BottomMenu';
import css from './leftSide.module.scss';
import NavigationMenu from './NavigationMenu';

const leftSideWidth = cssVariables['--leftSide-width'];

export class LeftSidebar extends Component {
  static propTypes = {
    userLocation: PropTypes.string,
    isLeftSideOpen: PropTypes.bool,
    isLeftSideDocked: PropTypes.bool,
    iHeartGuideId: PropTypes.string.isRequired,
    handleLeftSideToggle: PropTypes.func,
    breakpoint: PropTypes.number.isRequired,
    isAuthenticated: PropTypes.bool.isRequired,
    isDesktop: PropTypes.bool.isRequired,
    currentGuideId: PropTypes.string,
    subscriptionEnabled: PropTypes.bool.isRequired,
    authActions: PropTypes.shape({
      logout: PropTypes.func.isRequired,
      needsSignInDialog: PropTypes.func.isRequired,
      needsSignUpDialog: PropTypes.func.isRequired,
    }),
    actions: PropTypes.shape({
      logWebActivity: PropTypes.func.isRequired,
    }),
    userDetails: PropTypes.shape({
      userName: PropTypes.string,
    }),
    history: PropTypes.object.isRequired,
    isUserSubscribed: PropTypes.bool.isRequired,
    isExpiredWebSubscriber: PropTypes.bool.isRequired,
    shouldShowDesktopUpdateBanner: PropTypes.bool,
    topBannerHeight: PropTypes.number,
    isPlayerVisible: PropTypes.bool.isRequired,
    isFixedBottomMenuEnabled: PropTypes.bool.isRequired,
    gdprApplies: PropTypes.bool,
    bottomAdBannerHeight: PropTypes.number,
  };

  static contextType = LocationAndLocalizationContext;

  constructor(props) {
    super(props);

    this.layoutUpdateTimeoutId = null;
    this.drawerContainerRef = createRef();
    this.topSectionRef = createRef();
    this.bottomMenuRef = createRef();

    this.debouncedUpdateSidebarLayout = debounce(() => {
      if (this.props.isLeftSideOpen) {
        this.updateSidebarLayoutState();
      }
    }, 150);

    this.state = {
      isScrollShadowVisible: false,
      navStyles: this.calculateNavStyles(),
    };
  }

  componentDidMount() {
    this.scheduleSidebarLayoutUpdate();

    window.addEventListener('resize', this.debouncedUpdateSidebarLayout);
  }

  componentDidUpdate(prevProps) {
    const {
      isLeftSideOpen,
      gdprApplies,
      isPlayerVisible,
      bottomAdBannerHeight,
      isFixedBottomMenuEnabled,
    } = this.props;

    if (
      isLeftSideOpen !== prevProps.isLeftSideOpen ||
      (gdprApplies && !prevProps.gdprApplies) ||
      isPlayerVisible !== prevProps.isPlayerVisible ||
      bottomAdBannerHeight !== prevProps.bottomAdBannerHeight ||
      isFixedBottomMenuEnabled !== prevProps.isFixedBottomMenuEnabled
    ) {
      this.scheduleSidebarLayoutUpdate();
    }
  }

  componentWillUnmount() {
    if (this.layoutUpdateTimeoutId) {
      clearTimeout(this.layoutUpdateTimeoutId);
      this.layoutUpdateTimeoutId = null;
    }
    this.debouncedUpdateSidebarLayout.cancel();
    window.removeEventListener('resize', this.debouncedUpdateSidebarLayout);
  }

  scheduleSidebarLayoutUpdate = () => {
    if (this.layoutUpdateTimeoutId) {
      clearTimeout(this.layoutUpdateTimeoutId);
    }
    this.layoutUpdateTimeoutId = setTimeout(() => {
      this.layoutUpdateTimeoutId = null;
      this.updateSidebarLayoutState();
    }, 0);
  };

  calculateScrollShadowVisibility() {
    if (!this.props.isLeftSideOpen || !this.props.isFixedBottomMenuEnabled) {
      return false;
    }

    const { isScrollShadowVisible } = this.state;
    const scrollHeightOffset = isScrollShadowVisible ? 16 : 0;
    const topSectionEl = this.topSectionRef.current;

    if (!topSectionEl) {
      return isScrollShadowVisible;
    }

    return topSectionEl?.scrollHeight - scrollHeightOffset > topSectionEl?.clientHeight;
  }

  calculateNavStyles() {
    const { isPlayerVisible, isFixedBottomMenuEnabled } = this.props;

    const baseOffset = isFixedBottomMenuEnabled ? '0px' : '60px';
    const playerHeight = getCssStyle('--player-height');
    const playerHeightOffset = isPlayerVisible ? playerHeight : '0px';

    return {
      paddingBottom: `calc(${baseOffset} + ${playerHeightOffset})`,
    };
  }

  updateSidebarLayoutState = () => {
    const navStyles = this.calculateNavStyles();

    if (!this.props.isLeftSideOpen) {
      if (
        this.state.isScrollShadowVisible ||
        this.state.navStyles.paddingBottom !== navStyles.paddingBottom
      ) {
        this.setState({ isScrollShadowVisible: false, navStyles });
      }
      return;
    }

    const isScrollShadowVisible = this.calculateScrollShadowVisibility();
    if (
      isScrollShadowVisible !== this.state.isScrollShadowVisible ||
      this.state.navStyles.paddingBottom !== navStyles.paddingBottom
    ) {
      this.setState({ isScrollShadowVisible, navStyles });
    }
  };

  onMenuItemClick = (subLabel) => {
    this.props.actions.logWebActivity(web.actions.tap, appendToLabel(web.labels.leftNav, subLabel));
  };

  render() {
    const {
      isDesktop,
      subscriptionEnabled,
      isUserSubscribed,
      isExpiredWebSubscriber,
      userLocationCountry,
      iHeartGuideId,
      isLeftSideOpen,
      isLeftSideDocked,
      handleLeftSideToggle,
      authActions,
      isAuthenticated,
      breakpoint,
      currentGuideId,
      shouldShowDesktopUpdateBanner,
      topBannerHeight,
      history,
      isFixedBottomMenuEnabled,
      userDetails,
    } = this.props;
    const { getLocalizedText, location } = this.context;
    const { isScrollShadowVisible, navStyles } = this.state;

    const eventLabel = getSubscribeEventLabel(labels.leftsidenavUpsellDialog, location);
    const showUpsell = subscriptionEnabled && !isUserSubscribed;

    return (
      <div ref={this.drawerContainerRef}>
        <Drawer
          portalContainer={this.drawerContainerRef.current}
          open={isLeftSideOpen}
          side="left"
          variant={isLeftSideDocked ? 'persistent' : 'modal'}
          width={leftSideWidth}
          onOpenChange={handleLeftSideToggle}
          classNames={{
            portal: css.drawerPortal,
            backdrop: css.drawerBackdrop,
            content: css.drawerContent,
          }}
          styles={{
            content: shouldShowDesktopUpdateBanner ? getTopBannerOffsetStyles(topBannerHeight) : {},
          }}
          closeOnOutsidePress={!isLeftSideDocked}
        >
          <Scrollbar width={leftSideWidth}>
            <nav
              id="sidebarNav"
              data-testid="sidebarNav"
              className={classNames(css.nav, {
                [css.flexContainer]: isFixedBottomMenuEnabled,
                [css.isDesktop]: isDesktop,
              })}
              style={navStyles}
            >
              <div
                ref={this.topSectionRef}
                className={classNames(css.topSection, {
                  [css.bottomPadding]: isScrollShadowVisible,
                })}
              >
                {!isDesktop && (
                  <Link id="tuneInLogo" to="/" className={css.marqueeContainer}>
                    <TuneInMarquee
                      width="89px"
                      height="39px"
                      fill={cssVariables['--secondary-color-5']}
                    />
                  </Link>
                )}
                {showUpsell && (
                  <div
                    id="sidebarUpsellButtonContainer"
                    data-testid="sidebarUpsellButtonContainer"
                    className={css.upsellButtonContainer}
                  >
                    <UpsellButton
                      dataTestId="sidebarUpsellLink"
                      isExpiredWebSubscriber={isExpiredWebSubscriber}
                      source={eventLabel}
                      guideId={currentGuideId}
                    />
                  </div>
                )}
                <NavigationMenu
                  iHeartGuideId={iHeartGuideId}
                  isAuthenticated={isAuthenticated}
                  selectedGuideId={currentGuideId}
                  subscriptionEnabled={subscriptionEnabled}
                  onMenuItemClick={this.onMenuItemClick}
                  pathname={location.pathname}
                  getLocalizedText={getLocalizedText}
                  userDetails={userDetails}
                  userLocationCountry={userLocationCountry}
                />
              </div>
              <BottomMenu
                ref={this.bottomMenuRef}
                history={history}
                getLocalizedText={getLocalizedText}
                location={location}
                breakpoint={breakpoint}
                isDesktop={isDesktop}
                isAuthenticated={isAuthenticated}
                isScrollShadowVisible={isScrollShadowVisible}
                isFixedBottomMenuEnabled={isFixedBottomMenuEnabled}
                isLeftSideOpen={isLeftSideOpen}
                authActions={authActions}
                onMenuItemClick={this.onMenuItemClick}
              />
            </nav>
          </Scrollbar>
        </Drawer>
      </div>
    );
  }
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        logWebActivity,
      },
      dispatch,
    ),
  };
}

function mapStateToProps(state) {
  const { config, me } = state;

  return {
    userDetails: me?.details,
    userLocationCountry: config?.location?.country_code,
    iHeartGuideId: selectExperiment(state, IHEART_GUIDE_ID),
    isDesktop: selectIsDesktop(state),
    isUserSubscribed: selectIsUserSubscribed(state),
    isExpiredWebSubscriber: selectIsExpiredWebSubscriber(state),
    shouldShowDesktopUpdateBanner: selectShouldShowDesktopUpdateBanner(state),
    topBannerHeight: selectTopBannerHeight(state),
    isFixedBottomMenuEnabled: selectExperiment(state, LEFT_SIDEBAR_FIXED_BOTTOM_MENU),
    bottomAdBannerHeight: selectBottomAdBannerHeight(state),
  };
}

export default flowRight(
  withReactHooks(() => ({
    isPlayerVisible: useShouldShowPlayer(),
  })),
  connect(mapStateToProps, mapDispatchToProps),
  connectWithAuth,
)(LeftSidebar);
